import { useMemo } from 'react';

/**
 * Represents a job from the API
 */
export interface Job {
  jobId: string;
  executionArn?: string;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'QUEUED' | 'WAITING_FOR_INPUT' | 'CANCELLED' | 'COMPLETED' | 'TIMED_OUT' | 'PENDING';
  createdAt: string;
  startedAt?: string;
  displayName?: string;
  number?: string;
  step?: number;
  practice?: string;
  workflows?: string[];
  input?: {
    practice: string;
    workflows: string[];
    number: string;
    input_file_s3_uris?: string[];
  };
}

/**
 * A group of related jobs (e.g., multi-step workflow jobs with the same base number)
 */
export interface JobGroup {
  baseNumber: string;
  jobs: Job[];
}

/**
 * Extract base job number (removes -step-two, -step-three suffixes)
 */
export function getBaseJobNumber(job: Job): string {
  const number = job.displayName || job.number || job.input?.number || '';
  // Remove step suffixes like "-step-two", "-step-three", etc.
  return number.replace(/-step-(two|three|four|five|\d+)$/i, '');
}

/**
 * Get step number from job (1 for base, 2 for step-two, etc.)
 */
export function getStepNumber(job: Job): number {
  const number = job.displayName || job.number || job.input?.number || '';
  if (/-step-two$/i.test(number)) return 2;
  if (/-step-three$/i.test(number)) return 3;
  if (/-step-four$/i.test(number)) return 4;
  if (/-step-five$/i.test(number)) return 5;
  const match = number.match(/-step-(\d+)$/i);
  if (match) return parseInt(match[1], 10);
  return 1;
}

/**
 * Check if a job has an explicit step suffix (e.g. -step-two)
 */
function hasStepSuffix(job: Job): boolean {
  const number = job.displayName || job.number || job.input?.number || '';
  return /-step-(two|three|four|five|\d+)$/i.test(number);
}

/**
 * Group jobs by base number and sort by step.
 * Only groups jobs together when at least one has an explicit step suffix —
 * multiple standalone jobs with the same number are kept separate.
 */
export function groupJobs(jobs: Job[]): JobGroup[] {
  const groups = new Map<string, Job[]>();
  
  jobs.forEach((job) => {
    const baseNumber = getBaseJobNumber(job);
    if (!groups.has(baseNumber)) {
      groups.set(baseNumber, []);
    }
    groups.get(baseNumber)!.push(job);
  });
  
  // Sort jobs within each group by step number
  groups.forEach((groupJobs) => {
    groupJobs.sort((a, b) => getStepNumber(a) - getStepNumber(b));
  });

  // Split groups where no job has a step suffix — each job becomes its own group
  const finalGroups: [string, Job[]][] = [];
  groups.forEach((groupJobs, baseNumber) => {
    const anyHasStep = groupJobs.some(hasStepSuffix);
    if (anyHasStep) {
      finalGroups.push([baseNumber, groupJobs]);
    } else {
      // keep each job as its own standalone group
      groupJobs.forEach(job => {
        const key = job.displayName || job.number || job.input?.number || job.jobId;
        finalGroups.push([key, [job]]);
      });
    }
  });

  // Convert to array and sort groups by most recent job
  return finalGroups
    .map(([baseNumber, groupJobs]) => ({ baseNumber, jobs: groupJobs }))
    .sort((a, b) => {
      const aLatest = Math.max(...a.jobs.map(j => new Date(j.createdAt).getTime()));
      const bLatest = Math.max(...b.jobs.map(j => new Date(j.createdAt).getTime()));
      return bLatest - aLatest;
    });
}

/**
 * Filter jobs by status categories
 */
export function categorizeJobsByStatus(jobs: Job[]) {
  const sortByDate = (a: Job, b: Job) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

  const activeJobs = jobs
    .filter(job => ['RUNNING', 'QUEUED', 'WAITING_FOR_INPUT', 'PENDING'].includes(job.status))
    .sort(sortByDate);
  
  const failedJobs = jobs
    .filter(job => ['FAILED', 'TIMED_OUT', 'CANCELLED'].includes(job.status))
    .sort(sortByDate);
  
  const completedJobs = jobs
    .filter(job => ['SUCCEEDED', 'COMPLETED'].includes(job.status))
    .sort(sortByDate);

  return { activeJobs, failedJobs, completedJobs };
}

interface UseJobGroupingOptions {
  /** Group jobs by status categories (active, failed, completed) */
  groupByStatus?: boolean;
  /** Maximum number of completed groups to return */
  maxCompletedGroups?: number;
}

interface UseJobGroupingResult {
  /** All job groups (when not grouping by status) */
  allGroups: JobGroup[];
  /** Active job groups (when grouping by status) */
  activeGroups: JobGroup[];
  /** Failed job groups (when grouping by status) */
  failedGroups: JobGroup[];
  /** Completed job groups (when grouping by status) */
  completedGroups: JobGroup[];
  /** Total number of completed groups (before maxCompletedGroups limit) */
  totalCompletedGroups: number;
  /** Counts by category */
  counts: {
    active: number;
    failed: number;
    completed: number;
    total: number;
  };
}

/**
 * Hook to group and categorize jobs
 */
export function useJobGrouping(jobs: Job[], options: UseJobGroupingOptions = {}): UseJobGroupingResult {
  const { groupByStatus = false, maxCompletedGroups } = options;

  return useMemo(() => {
    if (groupByStatus) {
      const { activeJobs, failedJobs, completedJobs } = categorizeJobsByStatus(jobs);
      const activeGroups = groupJobs(activeJobs);
      const failedGroups = groupJobs(failedJobs);
      const allCompletedGroups = groupJobs(completedJobs);
      const completedGroups = maxCompletedGroups 
        ? allCompletedGroups.slice(0, maxCompletedGroups)
        : allCompletedGroups;

      return {
        allGroups: [],
        activeGroups,
        failedGroups,
        completedGroups,
        totalCompletedGroups: allCompletedGroups.length,
        counts: {
          active: activeJobs.length,
          failed: failedJobs.length,
          completed: completedJobs.length,
          total: jobs.length,
        },
      };
    }

    // Simple grouping without status categorization
    const allGroups = groupJobs(jobs);
    return {
      allGroups,
      activeGroups: [],
      failedGroups: [],
      completedGroups: [],
      totalCompletedGroups: 0,
      counts: {
        active: 0,
        failed: 0,
        completed: 0,
        total: jobs.length,
      },
    };
  }, [jobs, groupByStatus, maxCompletedGroups]);
}
